/**
 * SEO-hjälpare för receptsidan
 * Hanterar:
 * 1. Schema.org Recipe (JSON-LD) för Googles Rich Snippets (stjärnor, tillagningstid, bild, ingredienser i sökresultat)
 * 2. Dynamisk sidtitel (document.title)
 * 3. Meta-beskrivning och Open Graph-taggar (og:title, og:image, og:description) för delning på sociala medier
 */

function parseDurationToISO(timeStr) {
  if (!timeStr) return undefined
  const minMatch = timeStr.match(/(\d+)\s*(?:min|m)/i)
  if (minMatch) {
    return `PT${minMatch[1]}M`
  }
  const hourMatch = timeStr.match(/(\d+)\s*(?:tim|h)/i)
  if (hourMatch) {
    return `PT${hourMatch[1]}H`
  }
  return undefined
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setMetaProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function removeRecipeSchema() {
  const existing = document.getElementById('recipe-structured-data')
  if (existing) {
    existing.remove()
  }
}

function injectRecipeSchema(schemaData) {
  removeRecipeSchema()
  const script = document.createElement('script')
  script.id = 'recipe-structured-data'
  script.type = 'application/ld+json'
  script.text = JSON.stringify(schemaData, null, 2)
  document.head.appendChild(script)
}

export function updateSEOForRecipe(recipe, siteUrl = 'https://pannkakor.nu') {
  if (!recipe) {
    // Återställ till standard-SEO för startsidan
    document.title = 'Receptbanken – Goda recept för vardag och helg'
    setMeta('description', 'Enkla och goda recept för pannkakor, indiska rätter, pasta och husmanskost.')
    setMetaProperty('og:title', 'Receptbanken')
    setMetaProperty('og:description', 'Enkla och goda recept för pannkakor, indiska rätter, pasta och husmanskost.')
    setMetaProperty('og:url', `${siteUrl}/`)
    setMetaProperty('og:type', 'website')
    removeRecipeSchema()
    return
  }

  // 1. Sidtitel i webbläsaren och Google
  document.title = `${recipe.title} – Receptbanken`

  // 2. Meta-beskrivning
  if (recipe.description) {
    setMeta('description', recipe.description)
  }

  // 3. Open Graph (snygga förhandsvisningar vid länkdelning i SMS/iMessage/sociala medier)
  setMetaProperty('og:title', recipe.title)
  setMetaProperty('og:description', recipe.description || '')
  if (recipe.imageUrl) {
    setMetaProperty('og:image', recipe.imageUrl)
  }
  setMetaProperty('og:url', `${siteUrl}/recept/${recipe.id}`)
  setMetaProperty('og:type', 'article')

  // 4. Schema.org / JSON-LD för Google Recept-kort
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Recipe',
    'name': recipe.title,
    'description': recipe.description,
    'recipeCategory': recipe.categoryName,
    'prepTime': parseDurationToISO(recipe.prepTime),
    'cookTime': parseDurationToISO(recipe.cookTime),
    'totalTime': parseDurationToISO(recipe.totalTime),
    'recipeYield': `${recipe.servings} portioner`,
    'recipeIngredient': (recipe.ingredients || []).map(ing => {
      if (ing.amount) {
        return `${ing.amount} ${ing.unit} ${ing.name}`.trim()
      }
      return ing.name
    }),
    'recipeInstructions': (recipe.instructions || []).map((step, idx) => ({
      '@type': 'HowToStep',
      'position': idx + 1,
      'text': step
    }))
  }

  if (recipe.imageUrl) {
    schema.image = [recipe.imageUrl]
  }

  injectRecipeSchema(schema)
}
